/**
 * StructuredInput — bridge between ParameterInput's flat-string value
 * model and the structured PipelineEditor.
 *
 * Wire format is preserved: this component emits the same
 *   "#file#capability-inputs/<ts>-<name>.yaml"
 * sentinel the existing FileUploadInput emits. **Upload is deferred to
 * Run-click time** — the editor keeps the pending PipelineDoc in memory
 * and registers a commit handler via `PipelineSummaryContext`; the
 * drawer's Run handler awaits `commit()` before submitting the job.
 * This avoids the keystroke-rate S3 thrash the earlier debounced
 * approach produced and guarantees the YAML the backend reads is the
 * exact one the cost panel projected against.
 *
 * "Import from YAML" sub-mode hands off to the host's existing
 * file-upload renderer, so the file-upload code path is exercised
 * identically to flag-off mode.
 */

import {
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/solid';
import yaml from 'js-yaml';
import { type ReactNode, useCallback, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { useAggregatedAssetMetrics } from '@/hooks/useAssetMetrics';
import { CapabilityParameter } from '@/hooks/useCapabilities';
import { useUploadFile } from '@/hooks/useFiles';

import { PipelineEditor } from './PipelineEditor';
import { useRegisterPipelineCommit } from './PipelineSummaryContext';
import type { PipelineDoc, PipelineManifest, TargetMetrics } from './types';

interface StructuredInputProps {
  param: CapabilityParameter;
  manifest: PipelineManifest;
  value: string;
  onChange: (value: string) => void;
  /**
   * Render the file-upload escape hatch passthrough. The host returns
   * whatever its existing FileUploadInput would for this param.
   */
  renderUploadFallback: (param: CapabilityParameter) => ReactNode;
  target?: TargetMetrics;
  /**
   * Asset keys (e.g. `["#repository#URL#A", "#repository#URL#B"]`) for
   * the targets the capability will run against. The editor fetches
   * metrics for each in parallel and sums the per-target byte counts
   * so the cost panel projects TOTAL run cost across every selected
   * target (constantine runs one scan per target). Direct `target`
   * prop wins over fetched metrics if both are supplied (test seam).
   */
  assetKeys?: string[];
}

type UploadState =
  | { kind: 'pending' } // edits in memory, not yet uploaded
  | { kind: 'uploading' } // commit in flight (Run click → S3 PUT)
  | { kind: 'uploaded'; path: string } // commit finished, sentinel set
  | { kind: 'error'; message: string };

function generateUploadName(): string {
  return `capability-inputs/${Date.now()}-pipeline-config.yaml`;
}

export function StructuredInput({
  param,
  manifest,
  value,
  onChange,
  renderUploadFallback,
  target,
  assetKeys,
}: StructuredInputProps) {
  // Fetch real per-target metrics in parallel and sum across targets so
  // the cost panel projects TOTAL run cost (one scan per target). Direct
  // `target` prop wins (test seam); empty / loading / auth / pending
  // responses bubble up so the cost panel can render the right state.
  const aggregate = useAggregatedAssetMetrics(assetKeys ?? []);
  const effectiveTarget = target ?? aggregate.target;
  const metricsAuthRequired = !target && aggregate.authRequired;
  const metricsPending = !target && aggregate.pending;
  const [editorMode, setEditorMode] = useState<'editor' | 'upload'>('editor');
  const [uploadState, setUploadState] = useState<UploadState>({
    kind: 'pending',
  });
  const { mutateAsync: uploadFile } = useUploadFile();

  // Latest doc the editor has emitted. Held in memory only — never
  // uploaded until the drawer calls commit() at Run-click time.
  const pendingDocRef = useRef<PipelineDoc | null>(null);
  // YAML payload + path of the most recent successful commit, used to
  // short-circuit redundant commits when Run is clicked twice with no
  // edits in between.
  const lastCommittedRef = useRef<{ yaml: string; path: string } | null>(null);

  const handleDocChange = useCallback((doc: PipelineDoc) => {
    pendingDocRef.current = doc;
    // Flag the panel as pending iff the doc actually differs from the
    // last commit. Equal-doc no-op renders shouldn't bounce the chip.
    const candidate = yaml.dump(doc, { lineWidth: 120 });
    if (candidate === lastCommittedRef.current?.yaml) return;
    setUploadState({ kind: 'pending' });
  }, []);

  /**
   * One-shot upload, invoked by the drawer at Run-click time via
   * `PipelineSummaryContext.commit`. Returns the `#file#<path>`
   * sentinel the drawer writes into the parameter value.
   */
  const commit = useCallback(async (): Promise<string | null> => {
    if (editorMode === 'upload') {
      // Upload-mode passthrough: the host's FileUploadInput already
      // wrote a #file# sentinel via onChange. Use whatever `value`
      // currently holds — could be empty if user hasn't picked yet.
      return value || null;
    }
    const doc = pendingDocRef.current;
    if (!doc) return value || null;
    const yamlString = yaml.dump(doc, { lineWidth: 120 });
    if (yamlString === lastCommittedRef.current?.yaml) {
      const sentinel = `#file#${lastCommittedRef.current.path}`;
      onChange(sentinel);
      return sentinel;
    }
    setUploadState({ kind: 'uploading' });
    try {
      const uploadName = generateUploadName();
      await uploadFile({
        name: uploadName,
        content: yamlString,
        ignoreSnackbar: true,
      });
      lastCommittedRef.current = { yaml: yamlString, path: uploadName };
      setUploadState({ kind: 'uploaded', path: uploadName });
      const sentinel = `#file#${uploadName}`;
      onChange(sentinel);
      return sentinel;
    } catch (err) {
      // See the parent file's class-comment for why we prefer `.name`.
      const e = err instanceof Error ? err : null;
      const wrapperTitle =
        e && e.name && e.name !== 'Error' ? e.name : undefined;
      const message =
        wrapperTitle ?? e?.message ?? 'Pipeline upload failed (no detail)';
      setUploadState({ kind: 'error', message });
      throw err;
    }
  }, [editorMode, onChange, uploadFile, value]);

  useRegisterPipelineCommit(commit);

  const handleEnterUploadMode = () => {
    setUploadState({ kind: 'pending' });
    setEditorMode('upload');
  };

  const handleExitUploadMode = () => {
    setEditorMode('editor');
    onChange('');
    lastCommittedRef.current = null;
    setUploadState({ kind: 'pending' });
  };

  return (
    <div className="flex flex-col gap-3">
      {/* No top label — the editor visually IS the pipeline config.
          The "Import from YAML" toggle moves to the top-right corner
          of the editor body so it stays accessible without a
          redundant header row. */}
      <div className="flex items-center justify-end">
        <ModeToggle
          mode={editorMode}
          onEnterUpload={handleEnterUploadMode}
          onExitUpload={handleExitUploadMode}
        />
      </div>

      {editorMode === 'editor' ? (
        <>
          <PipelineEditor
            manifest={manifest}
            target={effectiveTarget}
            metricsAuthRequired={metricsAuthRequired}
            metricsPending={metricsPending}
            onDocChange={handleDocChange}
          />
          <UploadStatus state={uploadState} hasValue={Boolean(value)} />
        </>
      ) : (
        renderUploadFallback(param)
      )}
    </div>
  );
}

function ModeToggle({
  mode,
  onEnterUpload,
  onExitUpload,
}: {
  mode: 'editor' | 'upload';
  onEnterUpload: () => void;
  onExitUpload: () => void;
}) {
  if (mode === 'editor') {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="text-chariot-text-secondary hover:text-chariot-text-primary gap-1.5 text-xs"
        onClick={onEnterUpload}
      >
        <ArrowUpTrayIcon className="size-3.5" />
        Import from YAML
      </Button>
    );
  }
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="text-chariot-text-secondary hover:text-chariot-text-primary gap-1.5 text-xs"
      onClick={onExitUpload}
    >
      Back to editor
    </Button>
  );
}

function UploadStatus({
  state,
  hasValue,
}: {
  state: UploadState;
  hasValue: boolean;
}) {
  if (state.kind === 'uploading') {
    return (
      <span className="text-chariot-text-secondary flex items-center gap-1.5 text-xs">
        <CheckCircleIcon className="text-chariot-text-secondary size-3.5 animate-pulse" />
        Uploading pipeline...
      </span>
    );
  }
  if (state.kind === 'uploaded') {
    return (
      <span className="text-chariot-text-secondary flex items-center gap-1.5 text-xs">
        <CheckCircleIcon className="text-chariot-status-success size-3.5" />
        Pipeline saved
      </span>
    );
  }
  if (state.kind === 'pending') {
    // Pending = the user has edits the editor has not yet shipped to S3.
    // The upload only fires when Run is clicked (one shot, via the
    // PipelineSummaryContext commit handler) — at that point this
    // transitions to `uploading` and then `uploaded`.
    return (
      <span className="text-chariot-text-secondary flex items-center gap-1.5 text-xs">
        <PencilSquareIcon className="size-3.5" />
        {hasValue
          ? 'Pipeline edited — will upload on Run'
          : 'Pipeline will upload on Run'}
      </span>
    );
  }
  if (state.kind === 'error') {
    return (
      <span className="text-chariot-status-critical flex items-center gap-1.5 text-xs">
        <ExclamationCircleIcon className="size-3.5" />
        Failed to save pipeline: {state.message}
      </span>
    );
  }
  return null;
}
